import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { LabelingJob } from '../../types/labeling';

interface Submission {
  id: string;
  taskId: string;
  imageUrl?: string;
  mediaUrl?: string;
  category: string;
  taskType: string;
  taskTitle: string;
  createdAt: any;
  status: string;
  userId: string;
}

interface Task {
  id: string;
  title: string;
  detection?: {
    enabled: boolean;
    schemaRef?: {
      id: string;
      version: string;
    };
  };
}

export default function WorkbenchList() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load tasks with real-time listener
  useEffect(() => {
    console.log('🔍 [WorkbenchList] Setting up tasks listener...');
    const unsubscribeTasks = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        const tasksData: Record<string, Task> = {};
        snapshot.docs.forEach(doc => {
          tasksData[doc.id] = { id: doc.id, ...doc.data() } as Task;
        });
        setTasks(tasksData);
        console.log(`📊 [WorkbenchList] Loaded ${Object.keys(tasksData).length} tasks`);
      },
      (error) => {
        console.error('❌ [WorkbenchList] Error loading tasks:', error);
      }
    );
    
    return () => unsubscribeTasks();
  }, []);

  // Set up real-time listener for approved submissions (re-runs when tasks change)
  useEffect(() => {
    console.log('🔍 [WorkbenchList] Setting up submissions listener with', Object.keys(tasks).length, 'tasks...');
    
    // Only set up listener if we have tasks loaded
    if (Object.keys(tasks).length === 0) {
      console.log('⏳ [WorkbenchList] Waiting for tasks to load...');
      setSubmissions([]);
      setLoading(false);
      return;
    }
    
    const unsubscribeSubmissions = onSnapshot(
      // Items eligible for labeling: approved (ready) or needs_relabeling
      query(collection(db, 'submissions'), where('status', 'in', ['approved', 'needs_relabeling'])),
      (snapshot) => {
        console.log(`📊 [WorkbenchList] Received ${snapshot.docs.length} approved/needs_relabeling submissions from Firestore`);
        
        const submissionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Submission[];
        
        // Filter to only show submissions that have detection schemas
        console.log(`🔍 [WorkbenchList] Filtering ${submissionsData.length} submissions with ${Object.keys(tasks).length} tasks...`);
        const availableSubmissions = submissionsData.filter(submission => {
          const task = tasks[submission.taskId];
          const hasDetection = task?.detection?.enabled && task?.detection?.schemaRef?.id;
          
          console.log(`🔍 [WorkbenchList] Submission ${submission.id.slice(-8)}:`, {
            taskId: submission.taskId,
            task: task ? 'found' : 'not found',
            status: submission.status,
            hasDetection: hasDetection,
            detectionEnabled: task?.detection?.enabled,
            hasSchema: !!task?.detection?.schemaRef?.id,
            schemaId: task?.detection?.schemaRef?.id
          });
          
          if (!task) {
            console.log(`❌ [WorkbenchList] No task found for submission ${submission.id}`);
            return false;
          }
          
          if (!hasDetection) {
            console.log(`⚠️ [WorkbenchList] Submission ${submission.id.slice(-8)} filtered out - task ${submission.taskId} doesn't have detection enabled or schema assigned`);
            return false;
          }
          
          return true;
        });
        
        // Sort by creation date (oldest first)
        availableSubmissions.sort((a, b) => {
          const aTime = typeof a.createdAt === 'number' ? a.createdAt : 
                       (a.createdAt && typeof a.createdAt === 'object' && 'toMillis' in a.createdAt) ? 
                       (a.createdAt as any).toMillis() : 0;
          const bTime = typeof b.createdAt === 'number' ? b.createdAt : 
                       (b.createdAt && typeof b.createdAt === 'object' && 'toMillis' in b.createdAt) ? 
                       (b.createdAt as any).toMillis() : 0;
          return aTime - bTime;
        });
        
        console.log(`✅ [WorkbenchList] Filtered to ${availableSubmissions.length} available submissions for labeling`);
        setSubmissions(availableSubmissions);
        setLoading(false);
      },
      (error) => {
        console.error('❌ [WorkbenchList] Error in submissions listener:', error);
        setError(`Failed to load queue: ${error.message}`);
        setLoading(false);
      }
    );

    return () => {
      console.log('🧹 [WorkbenchList] Cleaning up submissions listener');
      unsubscribeSubmissions();
    };
  }, [tasks]);

  const getTaskName = (taskId: string) => {
    const task = tasks[taskId];
    return task ? task.title : 'Unknown Task';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading workbench queue...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Labeling Workbench</h1>
          <p className="text-gray-600 mt-1">
            Select a submission to start detection labeling.
          </p>
        </div>
        <button
          onClick={() => navigate('/labeling/queue')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          View Queue Status
        </button>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Submissions Available</h3>
          <p className="text-gray-600 mb-4">
            There are no approved submissions with detection schemas available for labeling.
          </p>
          <button
            onClick={() => navigate('/labeling/queue')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Check Queue Status
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {submissions.map((submission) => (
              <li key={submission.id} className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/labeling/workbench/${submission.id}`)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img 
                        src={submission.imageUrl || submission.mediaUrl} 
                        alt="Media" 
                        className="h-12 w-12 object-cover rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900">
                          Submission {submission.id.slice(-8)}
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Ready for Labeling
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Task: {getTaskName(submission.taskId)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Category: {submission.category}
                      </div>
                      <div className="text-sm text-gray-500">
                        Type: {submission.taskType}
                      </div>
                      <div className="text-sm text-gray-500">
                        Created: {new Date(
                          typeof submission.createdAt === 'number' ? submission.createdAt : 
                          (submission.createdAt && typeof submission.createdAt === 'object' && 'toMillis' in submission.createdAt) ? 
                          (submission.createdAt as any).toMillis() : 0
                        ).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      Click to open
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}